import { cn } from "@/lib/cn";
import debounce from "lodash.debounce";
import { forwardRef } from "react";

type BaseInputOptions = {
  placeholder: string;
  styles?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  maxLength?: number;
};

type TextInputOptions = BaseInputOptions & {
  type: "text";
};

type NumberInputOptions = BaseInputOptions & {
  type: "number";
  min: number;
  max: number;
};

type InputOptions = TextInputOptions | NumberInputOptions;

type InputProps = {
  value: string;
  directValue?: boolean;
  isValid?: boolean;
  emitValue: (value: string) => void;
  options?: InputOptions;
  debounce?: number;
};

const baseInputStyles =
  "tw-flex tw-h-9 tw-w-full tw-items-center tw-justify-start " +
  "tw-rounded tw-border tw-border-solid tw-border-[#e7e6ec] tw-bg-white " +
  "tw-px-3 tw-py-1 tw-font-sans tw-text-xs tw-font-normal tw-text-blue-base " +
  "placeholder:tw-text-[#88819f] focus:tw-outline focus:tw-outline-2 " +
  "focus:tw-outline-offset-2 focus:tw-outline-[#7b17cf33]";

export const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    value,
    isValid = true,
    emitValue,
    options,
    directValue,
    debounce: debounceTime = 0,
  } = props;

  const handleValue = debounce((newValue: string) => {
    if (options?.type !== "number") {
      return emitValue(newValue);
    }

    const numberValue = Number(newValue);
    if (numberValue <= options.min) return emitValue(options.min.toString());
    if (numberValue >= options.max) return emitValue(options.max.toString());
    emitValue(newValue);
  }, debounceTime);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (options?.type !== "number") return;

    const currentValue = e.currentTarget.value;
    const key = e.key;

    if (
      [
        "ArrowLeft",
        "ArrowUp",
        "ArrowDown",
        "ArrowRight",
        "Backspace",
        "Delete",
        "Tab",
        "Enter",
        "-",
        ".",
      ].includes(key)
    ) {
      return;
    }

    if (!/^\d$/.test(key)) {
      e.preventDefault();
      return;
    }

    const newValue = currentValue + key;
    const numberValue = Number(newValue);
    if (!Number.isNaN(numberValue) && numberValue > options.max) {
      e.preventDefault();
    }
  };

  const defaultNumericValue =
    options?.type === "number" && !value ? "0" : value;
  const inputValue = directValue
    ? { value: defaultNumericValue }
    : { defaultValue: defaultNumericValue };

  return (
    <input
      {...inputValue}
      ref={ref}
      onInput={(e) => handleValue(e.currentTarget.value)}
      onKeyDown={handleKeyDown}
      maxLength={options?.maxLength ?? undefined}
      onFocus={options?.onFocus}
      onBlur={options?.onBlur}
      className={cn(baseInputStyles, {
        [options?.styles || ""]: Boolean(options?.styles),
        "tw-border-[#d43548] tw-bg-[#faf1f2] tw-text-[#d43548] placeholder:tw-text-[#d43548b2]":
          !isValid,
        "[&::-webkit-inner-spin-button]:!tw-appearance-auto [&::-webkit-outer-spin-button]:!tw-appearance-auto":
          options?.type === "number",
      })}
      type={options?.type || "text"}
      placeholder={options?.placeholder || ""}
      min={options?.type === "number" ? options.min : undefined}
      max={options?.type === "number" ? options.max : undefined}
    />
  );
});

Input.displayName = "Input";
